import React from "react";
import dayjs from "dayjs";

import ChartDaysWorked from "./ChartDaysWorked.tsx";
import ModalContainer from "../../components/ModalContainer.tsx";
import { IPayrollReport } from "../../models/Payroll";
import PartnerCell, { toTitleCase } from "./PartnerCell.tsx";
import TotalIncidents from "./TotalIncidents.tsx";
import { Link } from "react-router-dom";
import PaymentDetail from "./PaymentDetail.tsx";
import {
  calculateExtraHoursLikeChart,
  formatHoursToTime,
  formatPayrollHoursDecimal,
  calculateDailyPayment,
  roundHours,
} from "../../services/payroll/ui.ts";
import {
  hasAcceptedFreeDayWorkedIncident,
  withFreeDayWorkedPremium,
} from "../../services/payroll/payPremiums.ts";
import { formatDataForReport } from "../../utils/reporteChequeoUtils.ts";
import { calcularTiempoTrabajado } from "../../interfaces/formik/IReporteRelojChecador.ts";
import * as moment from "moment-timezone";
import { useCalculateTotalPay } from "../../hooks/useCalculateTotalPay.tsx";
import {
  mergeChartDetailedByDate,
  mergeDailyPaymentsByDate,
} from "../../utils/payrollVisitHours.ts";
import {
  chartHorasExtrasExcludingDoubleShift,
  computeTotalPayWithIncentivesFromDetail,
  getPartnerHoursByDate,
  scheduleHasRejectedExtraPayIncident,
  summarizeChartDays,
  splitPayrollDeductionTotalsForCuadro,
  formatPayrollMoneyAmount,
  sumExtraHoursFromDailyPayments,
} from "../../utils/payrollUtils.ts";
import { adjustChartDetailedPayrollExtras } from "../../utils/payrollRhPending.ts";
import UpdateSalary from "../../components/profile/UpdateSalary.tsx";
import { getInfoPositionPartner } from "../../services/partner.service.ts";
import {
  findChartDaysWorked,
  findPaymentDetail,
  isPayrollFetchAborted,
  type PayrollRowDetailsSnapshot,
} from "../../services/payroll.service.ts";
import type { PartnerIncidentCountSnapshot } from "../../services/incident.service.ts";
import {
  buildVisibleIncidentDateSet,
  filterIncidentsInPayrollDateRange,
} from "../../utils/incidentLeaderReviewFilter.ts";
import { buildPayrollRhPendingFromSnapshot } from "../../utils/payrollRhPending.ts";
import { notification } from "../../utils/Notifications.tsx";
import { useDispatch } from "react-redux";
import { updateRecordByIndex } from "../../store/slices/payrollSlice.ts";
import { getCurrencyFormat } from "../../utils/index.ts";
import { Incident } from "../../models/Incident";
import { useFullDayHours } from "../../hooks/useFullDayHours.tsx";
import { usePayrollBranchMeta } from "../../hooks/usePayrollBranchMeta.ts";
import type { BranchReportMeta } from "../../utils/payrollBranchMeta.ts";

const calculationCache = new Map();

/** Valores tal como se muestran en la tabla del cuadro de mando (export Excel). */
export type CuadroMandoTableRowSnapshot = {
  chilitoText: string;
  daysWorked: string | number;
  jornadasCompletadas: number;
  horasTrabajadas: string;
  horasNormales: string;
  turnosDobles: number;
  horasExtras: string;
  incidentsTotal: number;
  pagoEstimado: string;
};

const RowPayRoll = ({
  record,
  dates,
  branch,
  detailedRecords,
  realCheckRecords,
  onRefreshPartner,
  isSpecial,
  onCuadroExportSnapshot,
  fullDayHours: fullDayHoursProp,
  payrollBranchMeta: payrollBranchMetaProp,
  incidentCountSnapshot,
  rowDetailsSnapshot,
  rowDetailsAwaitingBatch,
}: {
  record: IPayrollReport;
  dates: { start: dayjs.Dayjs; end: dayjs.Dayjs };
  branch: number;
  detailedRecords: any[];
  realCheckRecords?: any[];
  onRefreshPartner?: (partnerId: number) => Promise<void>;
  isSpecial?: boolean;
  onCuadroExportSnapshot?: (
    partnerId: number,
    snapshot: CuadroMandoTableRowSnapshot | null
  ) => void;
  /** Evita N peticiones HTTP duplicadas cuando PayrollBranch ya cargó el valor. */
  fullDayHours?: number;
  /** Meta de sucursal (turno nocturno); una sola carga en PayrollBranch. */
  payrollBranchMeta?: Record<number, BranchReportMeta>;
  /** Conteo + pendientes del líder (carga batch en PayrollBranch). */
  incidentCountSnapshot?: PartnerIncidentCountSnapshot | null;
  /** Detalle precargado por GET /payroll/row-details-batch (evita 2×N peticiones). */
  rowDetailsSnapshot?: PayrollRowDetailsSnapshot | null;
  /** true = esperar batch del padre; no disparar payment-detail/chart por fila. */
  rowDetailsAwaitingBatch?: boolean;
}) => {
  const dispatch = useDispatch();
  const [modal, setModal] = React.useState({ open: false, type: "" });
  const [modalSalary, setModalSalary] = React.useState(false);
  const [loadingSalary, setLoadingSalary] = React.useState(false);
  const [salaryData, setSalaryData] = React.useState<any>({});
  const [chartDaysData, setChartDaysData] = React.useState<{
    detailed: any[];
    graph: any[];
    freeDaysDates?: string[];
    freeDaysEnjoyedDates?: string[];
    celebrityDaysDates?: string[];
    absenceRows?: any[];
    savedIncidents?: any[];
  } | null>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [detailsLoading, setDetailsLoading] = React.useState(false);
  const detailsAbortRef = React.useRef<AbortController | null>(null);

  const { fullDayHours: fullDayHoursFromHook } = useFullDayHours(
    branch,
    fullDayHoursProp
  );
  const fullDayHours = React.useMemo(() => {
    const value =
      typeof fullDayHoursFromHook === "number" &&
      !isNaN(fullDayHoursFromHook) &&
      fullDayHoursFromHook > 0
        ? fullDayHoursFromHook
        : 8.5;
    return value;
  }, [fullDayHoursFromHook]);
  const payrollBranchMetaFromHook = usePayrollBranchMeta(
    payrollBranchMetaProp ? [] : realCheckRecords,
    branch
  );
  const payrollBranchMeta = payrollBranchMetaProp ?? payrollBranchMetaFromHook;

  /* New state for detailed calculation */
  const [paymentDetailData, setPaymentDetailData] = React.useState<{
    dailyPayments?: any[];
    realCheckRecords?: any[];
    schedules?: any[];
    realSchedule?: any[];
    paymentDetails?: any;
    calculatedBonuses?: any[];
    calculatedNewDeductions?: any[];
    freeDaysEnjoyedDates?: string[];
    absenceRows?: any[];
  } | null>(null);
  const [paymentDetailDataVersion, setPaymentDetailDataVersion] =
    React.useState(0);

  const applyFreshPaymentAndChart = React.useCallback(
    async (options?: { refresh?: boolean }): Promise<
      | {
          paymentRes: Awaited<ReturnType<typeof findPaymentDetail>>;
          chartResponse: Awaited<ReturnType<typeof findChartDaysWorked>>;
        }
      | undefined
    > => {
      if (!record?.partnerId || !branch || !dates?.start || !dates?.end) {
        return undefined;
      }
      const startDate = dates.start.format("YYYY-MM-DD");
      const endDate = dates.end.format("YYYY-MM-DD");
      const refreshOpts = options?.refresh ? { refresh: true as const } : undefined;
      const [paymentRes, chartResponse] = await Promise.all([
        findPaymentDetail(
          record.partnerId,
          branch,
          startDate,
          endDate,
          undefined,
          refreshOpts
        ),
        findChartDaysWorked(
          record.partnerId,
          branch,
          startDate,
          endDate,
          undefined,
          refreshOpts
        ),
      ]);

      setPaymentDetailData({
        dailyPayments: mergeDailyPaymentsByDate(paymentRes.dailyPayments || []),
        realCheckRecords: paymentRes.realCheckRecords || [],
        schedules: paymentRes.schedules || [],
        realSchedule: paymentRes.realSchedule || [],
        paymentDetails: paymentRes.paymentDetails || {},
        calculatedBonuses: paymentRes.calculatedBonuses || [],
        calculatedNewDeductions: paymentRes.calculatedNewDeductions || [],
        freeDaysEnjoyedDates: paymentRes.freeDaysEnjoyedDates || [],
        absenceRows: paymentRes.absenceRows || [],
      });

      const savedIncidents = chartResponse?.savedIncidents || [];
      const adjustedDetailed = adjustChartDetailedPayrollExtras(
        chartResponse.detailed || [],
        savedIncidents,
        incidentCountSnapshot
      );
      const absenceDetailed = (chartResponse?.absenceRows || []).map(
        (row: any) => ({
          id: 0,
          fecha: row.date,
          entradaProgramada: row.entradaProgramada || "N/A",
          salidaProgramada: row.salidaProgramada || "N/A",
          entradaRegistrada: "N/A",
          salidaRegistrada: "N/A",
          horasTrabajadasEnHorario: 0,
          horasRegistradas: 0,
          horasExtras: 0,
          isAbsence: true,
        })
      );
      const detailedWithAbsences = mergeChartDetailedByDate(
        [...adjustedDetailed, ...absenceDetailed]
      ).sort(
        (a, b) =>
          moment(a.fecha, "DD-MM-YYYY").valueOf() -
          moment(b.fecha, "DD-MM-YYYY").valueOf()
      );
      setChartDaysData({
        ...chartResponse,
        detailed: detailedWithAbsences,
        savedIncidents,
      });
      setPaymentDetailDataVersion((v) => v + 1);
      return { paymentRes, chartResponse };
    },
    [
      record.partnerId,
      branch,
      incidentCountSnapshot,
      dates?.start?.format("YYYY-MM-DD"),
      dates?.end?.format("YYYY-MM-DD"),
    ]
  );

  const syncCuadroTotalFromDetail = React.useCallback(
    (
      paymentRes: {
        dailyPayments?: any[];
        freeDaysEnjoyedDates?: string[];
      },
      chartResponse?: {
        freeDaysDates?: string[];
        savedIncidents?: any[];
      }
    ) => {
      const totalNum = computeTotalPayWithIncentivesFromDetail({
        paymentDetail: {
          dailyPayments: paymentRes.dailyPayments || [],
          freeDaysEnjoyedDates: paymentRes.freeDaysEnjoyedDates || [],
        },
        chartFreeDaysDates: chartResponse?.freeDaysDates || [],
        savedIncidents: chartResponse?.savedIncidents || [],
        record,
        fullDayHours,
      });
      if (record.index === undefined) return;
      dispatch(
        updateRecordByIndex({
          index: record.index,
          data: {
            ...record,
            totalPayWithIncentives: getCurrencyFormat(totalNum),
          },
        })
      );
    },
    [record, fullDayHours, dispatch]
  );

  const refreshPartnerPayrollData = React.useCallback(
    async (partnerId: number) => {
      if (onRefreshPartner) {
        await onRefreshPartner(partnerId);
      }
      const fresh = await applyFreshPaymentAndChart({ refresh: true });
      if (fresh?.paymentRes) {
        syncCuadroTotalFromDetail(fresh.paymentRes, fresh.chartResponse);
      }
      setPaymentDetailDataVersion((v) => v + 1);
    },
    [onRefreshPartner, applyFreshPaymentAndChart, syncCuadroTotalFromDetail]
  );

  /** Detalle completo del snapshot (cuadro + modal usan la misma fuente). */
  const applySnapshotDetail = React.useCallback(
    (snapshotDetail: {
      paymentDetail?: Record<string, unknown>;
      chartDaysWorked?: Record<string, unknown>;
    }) => {
      const payment = snapshotDetail.paymentDetail;
      const chart = snapshotDetail.chartDaysWorked;
      if (payment) {
        setPaymentDetailData({
          dailyPayments: mergeDailyPaymentsByDate(
            (payment.dailyPayments as any[]) || []
          ),
          realCheckRecords: (payment.realCheckRecords as any[]) || [],
          schedules: (payment.schedules as any[]) || [],
          realSchedule: (payment.realSchedule as any[]) || [],
          paymentDetails: (payment.paymentDetails as any) || {},
          calculatedBonuses: (payment.calculatedBonuses as any[]) || [],
          calculatedNewDeductions:
            (payment.calculatedNewDeductions as any[]) || [],
          freeDaysEnjoyedDates: (payment.freeDaysEnjoyedDates as string[]) || [],
          absenceRows: (payment.absenceRows as any[]) || [],
        });
      }
      if (chart) {
        const savedIncidents = (chart.savedIncidents as any[]) || [];
        const adjustedDetailed = adjustChartDetailedPayrollExtras(
          (chart.detailed as any[]) || [],
          savedIncidents,
          incidentCountSnapshot
        );
        const absenceDetailed = ((chart.absenceRows as any[]) || []).map(
          (row: any) => ({
            id: 0,
            fecha: row.date,
            entradaProgramada: row.entradaProgramada || "N/A",
            salidaProgramada: row.salidaProgramada || "N/A",
            entradaRegistrada: "N/A",
            salidaRegistrada: "N/A",
            horasTrabajadasEnHorario: 0,
            horasRegistradas: 0,
            horasExtras: 0,
            isAbsence: true,
          })
        );
        const detailedWithAbsences = [...adjustedDetailed, ...absenceDetailed].sort(
          (a, b) =>
            moment(a.fecha, "DD-MM-YYYY").valueOf() -
            moment(b.fecha, "DD-MM-YYYY").valueOf()
        );
        setChartDaysData({
          ...(chart as any),
          detailed: detailedWithAbsences,
          savedIncidents,
        });
      }
      setPaymentDetailDataVersion((v) => v + 1);
    },
    [incidentCountSnapshot]
  );

  React.useEffect(() => {
    if (!chartDaysData?.detailed?.length) return;
    const savedIncidents = chartDaysData.savedIncidents || [];
    const adjusted = adjustChartDetailedPayrollExtras(
      chartDaysData.detailed,
      savedIncidents,
      incidentCountSnapshot
    );
    const extrasChanged = adjusted.some(
      (row, index) =>
        Number(row?.horasExtras ?? 0) !==
        Number(chartDaysData.detailed?.[index]?.horasExtras ?? 0)
    );
    if (!extrasChanged) return;
    setChartDaysData((prev) =>
      prev ? { ...prev, detailed: adjusted } : prev
    );
  }, [
    chartDaysData?.detailed,
    chartDaysData?.savedIncidents,
    incidentCountSnapshot,
  ]);

  const resolveRowSnapshot = React.useCallback(():
    | {
        paymentDetail: Record<string, unknown>;
        chartDaysWorked: Record<string, unknown>;
      }
    | null => {
    if (rowDetailsSnapshot?.paymentDetail && rowDetailsSnapshot?.chartDaysWorked) {
      return rowDetailsSnapshot;
    }
    const embedded = (record as { snapshotDetail?: typeof rowDetailsSnapshot })
      .snapshotDetail;
    if (embedded?.paymentDetail && embedded?.chartDaysWorked) {
      return embedded;
    }
    return null;
  }, [rowDetailsSnapshot, record]);

  const loadRowDetails = React.useCallback(async () => {
    if (!record?.partnerId || !branch || !dates?.start || !dates?.end) {
      return;
    }

    const cachedSnapshot = resolveRowSnapshot();
    if (cachedSnapshot) {
      applySnapshotDetail(cachedSnapshot);
      return;
    }

    if (rowDetailsAwaitingBatch) {
      return;
    }

    detailsAbortRef.current?.abort();
    const ac = new AbortController();
    detailsAbortRef.current = ac;
    setDetailsLoading(true);

    try {
      await applyFreshPaymentAndChart();
    } catch (error) {
      if (isPayrollFetchAborted(error)) return;
      console.error("Error loading payroll row details:", error);
    } finally {
      if (!ac.signal.aborted) {
        setDetailsLoading(false);
      }
      if (detailsAbortRef.current === ac) {
        detailsAbortRef.current = null;
      }
    }
  }, [
    record.partnerId,
    branch,
    dates?.start?.format("YYYY-MM-DD"),
    dates?.end?.format("YYYY-MM-DD"),
    refreshKey,
    applyFreshPaymentAndChart,
    applySnapshotDetail,
    resolveRowSnapshot,
    rowDetailsAwaitingBatch,
  ]);

  React.useEffect(() => {
    const snap = resolveRowSnapshot();
    if (!snap?.paymentDetail || !snap?.chartDaysWorked) return;
    React.startTransition(() => {
      applySnapshotDetail(snap);
    });
  }, [resolveRowSnapshot, applySnapshotDetail]);

  React.useEffect(() => {
    if (rowDetailsAwaitingBatch) {
      return;
    }
    void loadRowDetails();
    return () => detailsAbortRef.current?.abort();
  }, [loadRowDetails, rowDetailsAwaitingBatch]);

  const openModal = React.useCallback(
    (type: string) => {
      const snap = resolveRowSnapshot();
      if (snap) {
        applySnapshotDetail(snap);
      }
      setModal({ open: true, type });
      if (!snap && !rowDetailsAwaitingBatch) {
        void applyFreshPaymentAndChart({ refresh: true });
      }
    },
    [
      applyFreshPaymentAndChart,
      applySnapshotDetail,
      resolveRowSnapshot,
      rowDetailsAwaitingBatch,
    ]
  );

  const effectiveSchedules =
    paymentDetailData?.schedules || record.schedules || [];
  const effectiveRealSchedule =
    paymentDetailData?.realSchedule || record.realSchedule || [];
  const effectiveRealCheckRecords =
    paymentDetailData?.realCheckRecords || realCheckRecords || [];

  const calculateTotalPayWithIncentives = React.useMemo(() => {
    if (!paymentDetailData?.dailyPayments?.length) {
      return record.totalPayWithIncentives || "$0.00";
    }
    const totalNum = computeTotalPayWithIncentivesFromDetail({
      paymentDetail: paymentDetailData,
      chartFreeDaysDates: chartDaysData?.freeDaysDates,
      savedIncidents: chartDaysData?.savedIncidents,
      record,
      fullDayHours,
    });
    return getCurrencyFormat(totalNum);
  }, [
    paymentDetailData,
    chartDaysData?.freeDaysDates,
    chartDaysData?.savedIncidents,
    record,
    fullDayHours,
    paymentDetailDataVersion,
  ]);

  // Usar el cálculo nuevo (igual que PaymentDetail.tsx), con fallback al hook anterior si no hay datos
  const { totalPayFormatted: fallbackTotalPay } = useCalculateTotalPay({
    data: {
      ...record,
      schedules: effectiveSchedules,
      realSchedule: effectiveRealSchedule,
    },
    detailedRecords,
    dates,
    realCheckRecords: effectiveRealCheckRecords,
    useSimpleCalculation: !paymentDetailData?.dailyPayments?.length,
    fullDayHours,
  });

  const handleRefreshPartner = React.useCallback(
    async (partnerId: number) => {
      if (onRefreshPartner) {
        await onRefreshPartner(partnerId);
      }
      setPaymentDetailData(null);
      setChartDaysData(null);
      setRefreshKey((prev) => prev + 1);
    },
    [onRefreshPartner]
  );

  React.useEffect(() => {
    if (refreshKey > 0) {
      void loadRowDetails();
    }
  }, [refreshKey, loadRowDetails]);

  const handleEditSalary = async () => {
    setModalSalary(true);
    try {
      const response = await getInfoPositionPartner(record.partnerId);
      setSalaryData(response);
    } catch (error) {
      notification(
        "Error",
        "Error al obtener información del salario",
        "danger"
      );
    }
  };

  const handleRefreshSalary = async () => {
    try {
      const response = await getInfoPositionPartner(record.partnerId);
      setSalaryData(response);

      const newMonthlySalary = response.candidate.monthlySalary;
      const dayToPay = 15;
      const dailySalary = newMonthlySalary / dayToPay;
      const hourlyPay = dailySalary / fullDayHours;
      const salaryPerFortnight = dailySalary * 15;

      const updatedRecord: IPayrollReport = {
        ...record,
        salary: getCurrencyFormat(newMonthlySalary),
        dailySalary: getCurrencyFormat(dailySalary),
        payPerHour: getCurrencyFormat(hourlyPay),
        salaryPerFortnight: getCurrencyFormat(salaryPerFortnight),
      };

      dispatch(
        updateRecordByIndex({
          index: record.index,
          data: updatedRecord,
        })
      );

      if (onRefreshPartner) {
        await onRefreshPartner(record.partnerId);
      }
    } catch (error) {
      notification(
        "Error",
        "Error al actualizar los valores de nómina",
        "danger"
      );
    }
  };

  const chartSummary = React.useMemo(
    () => summarizeChartDays(chartDaysData?.detailed || [], fullDayHours),
    [chartDaysData?.detailed, fullDayHours]
  );

  const extraHoursFromPayments = React.useMemo(() => {
    const payments = paymentDetailData?.dailyPayments;
    if (!payments?.length) {
      return null;
    }
    return sumExtraHoursFromDailyPayments(payments, fullDayHours);
  }, [
    paymentDetailData?.dailyPayments,
    fullDayHours,
    paymentDetailDataVersion,
  ]);

  const filteredIncidentsCount = React.useMemo(() => {
    if (!record.schedules || !Array.isArray(record.schedules)) {
      return record.totalIncidents || 0;
    }

    const allIncidents: Incident[] = [];
    record.schedules.forEach((schedule: any) => {
      if (schedule.incidents && Array.isArray(schedule.incidents)) {
        allIncidents.push(...schedule.incidents);
      }
    });

    if (allIncidents.length === 0) {
      return record.totalIncidents || 0;
    }

    let filteredArray = allIncidents;
    if (dates) {
      const startDateUTC = moment
        .utc(dates.start.format("YYYY-MM-DD"))
        .startOf("day");
      const endDateUTC = moment
        .utc(dates.end.format("YYYY-MM-DD"))
        .endOf("day");

      filteredArray = allIncidents.filter((incident) => {
        const incidentDate =
          incident.schedule?.assistanceCheck?.checkDate ||
          incident.schedule?.exactDate;
        if (!incidentDate) return false;
        const incidentDateUTC = moment.utc(incidentDate).startOf("day");
        return (
          incidentDateUTC.isSameOrAfter(startDateUTC, "day") &&
          incidentDateUTC.isSameOrBefore(endDateUTC, "day")
        );
      });
    }

    let validCount = 0;
    for (const incident of filteredArray) {
      const check = incident.schedule?.assistanceCheck?.checkTime;
      const checkout = incident.schedule?.assistanceCheckout?.checkTime;
      const start = incident.schedule?.startTime?.split(" - ")[0];
      const end = incident.schedule?.endTime?.split(" - ")[1];

      if (check && checkout && start && end) {
        validCount++;
      }
    }

    return validCount;
  }, [
    record.schedules,
    record.totalIncidents,
    dates?.start?.format("YYYY-MM-DD"),
    dates?.end?.format("YYYY-MM-DD"),
  ]);

  const scheduleIncidents = React.useMemo(() => {
    if (!record.schedules || !Array.isArray(record.schedules)) {
      return [] as any[];
    }
    const all: any[] = [];
    record.schedules.forEach((schedule: any) => {
      if (schedule.incidents?.length) {
        all.push(...schedule.incidents);
      }
    });
    return all;
  }, [record.schedules]);

  const visiblePayrollIncidents = React.useMemo(() => {
    if (!dates?.start || !dates?.end) {
      return filterIncidentsInPayrollDateRange(scheduleIncidents, "", "");
    }
    return filterIncidentsInPayrollDateRange(
      scheduleIncidents,
      dates.start.format("YYYY-MM-DD"),
      dates.end.format("YYYY-MM-DD")
    );
  }, [
    scheduleIncidents,
    dates?.start?.format("YYYY-MM-DD"),
    dates?.end?.format("YYYY-MM-DD"),
  ]);

  const [modalIncidentsCount, setModalIncidentsCount] = React.useState<
    number | null
  >(null);

  React.useEffect(() => {
    setModalIncidentsCount(null);
  }, [
    record.partnerId,
    dates?.start?.format("YYYY-MM-DD"),
    dates?.end?.format("YYYY-MM-DD"),
    incidentCountSnapshot?.displayTotal,
    incidentCountSnapshot?.total,
  ]);

  const visibleIncidentsCount = React.useMemo(() => {
    const embedded = visiblePayrollIncidents.length;
    const chartIncidents = filterIncidentsInPayrollDateRange(
      chartDaysData?.savedIncidents || [],
      dates.start.format("YYYY-MM-DD"),
      dates.end.format("YYYY-MM-DD")
    );
    const fromChart = chartIncidents.length;
    const fromSnapshot =
      incidentCountSnapshot?.displayTotal ?? incidentCountSnapshot?.total;

    const candidates = [
      modalIncidentsCount,
      fromSnapshot,
      embedded,
      fromChart,
    ].filter((n): n is number => typeof n === "number" && n >= 0);

    return candidates.length > 0 ? Math.max(...candidates) : 0;
  }, [
    visiblePayrollIncidents.length,
    chartDaysData?.savedIncidents,
    dates?.start?.format("YYYY-MM-DD"),
    dates?.end?.format("YYYY-MM-DD"),
    incidentCountSnapshot?.displayTotal,
    incidentCountSnapshot?.total,
    modalIncidentsCount,
  ]);

  const visiblePendingDotDates = React.useMemo(() => {
    const incidentSource =
      scheduleIncidents.length > 0
        ? visiblePayrollIncidents
        : filterIncidentsInPayrollDateRange(
            chartDaysData?.savedIncidents || [],
            dates.start.format("YYYY-MM-DD"),
            dates.end.format("YYYY-MM-DD")
          );
    if (
      scheduleIncidents.length > 0 ||
      (chartDaysData?.savedIncidents?.length ?? 0) > 0
    ) {
      return Array.from(
        buildPayrollRhPendingFromSnapshot(incidentSource, incidentCountSnapshot)
          .rhPending
      );
    }
    if (incidentCountSnapshot?.pendingDates?.length) {
      return [
        ...(incidentCountSnapshot.pendingDates || []),
        ...(incidentCountSnapshot.noProgramadoDates || []),
      ];
    }
    return undefined as string[] | undefined;
  }, [
    scheduleIncidents.length,
    visiblePayrollIncidents,
    chartDaysData?.savedIncidents,
    incidentCountSnapshot,
    dates?.start?.format("YYYY-MM-DD"),
    dates?.end?.format("YYYY-MM-DD"),
  ]);

  const chartDayFechas = React.useMemo(
    () =>
      new Set(
        (chartDaysData?.detailed || [])
          .map((row: { fecha?: string }) => row.fecha)
          .filter(Boolean) as string[]
      ),
    [chartDaysData?.detailed]
  );

  const extraIncidentDates = React.useMemo(() => {
    if (scheduleIncidents.length > 0 || (chartDaysData?.savedIncidents?.length ?? 0) > 0) {
      const source =
        scheduleIncidents.length > 0
          ? visiblePayrollIncidents
          : filterIncidentsInPayrollDateRange(
              chartDaysData?.savedIncidents || [],
              dates.start.format("YYYY-MM-DD"),
              dates.end.format("YYYY-MM-DD")
            );
      return Array.from(buildVisibleIncidentDateSet(source)).filter(
        (dateKey) => !chartDayFechas.has(dateKey)
      );
    }
    return incidentCountSnapshot?.extraDates ?? [];
  }, [
    scheduleIncidents.length,
    visiblePayrollIncidents,
    chartDaysData?.savedIncidents,
    chartDayFechas,
    dates?.start?.format("YYYY-MM-DD"),
    dates?.end?.format("YYYY-MM-DD"),
    incidentCountSnapshot?.extraDates,
  ]);

  const legacyDaysWorked = React.useMemo(() => {
    if (realCheckRecords && realCheckRecords.length > 0) {
      const formattedData = formatDataForReport(
        realCheckRecords,
        dates.start.format("YYYY-MM-DD"),
        dates.end.format("YYYY-MM-DD"),
        payrollBranchMeta
      );

      const partnerData = formattedData.filter(
        (checkRecord) => checkRecord.checkIn?.partner_id === record.partnerId
      );

      return partnerData.filter(
        (checkRecord) => checkRecord.checkIn && checkRecord.checkOut
      ).length;
    } else if (detailedRecords && detailedRecords.length > 0) {
      const partnerRecords = getPartnerHoursByDate(
        detailedRecords,
        record.partnerId
      );
      return partnerRecords.length;
    } else if (record.realSchedule && record.realSchedule.length > 0) {
      return record.realSchedule.length;
    } else {
      return record.daysWorked || 0;
    }
  }, [
    realCheckRecords,
    detailedRecords,
    record.partnerId,
    record.realSchedule,
    record.daysWorked,
    dates.start.format("YYYY-MM-DD"),
    dates.end.format("YYYY-MM-DD"),
  ]);

  const legacyCompleteDaysWorked = React.useMemo(() => {
    if (
      record.jornadasCompletas !== undefined &&
      record.jornadasCompletas !== null
    ) {
      return record.jornadasCompletas;
    }

    if (realCheckRecords && realCheckRecords.length > 0) {
      const formattedData = formatDataForReport(
        realCheckRecords,
        dates.start.format("YYYY-MM-DD"),
        dates.end.format("YYYY-MM-DD"),
        payrollBranchMeta
      );

      const partnerData = formattedData.filter(
        (checkRecord) => checkRecord.checkIn?.partner_id === record.partnerId
      );

      const startDateUTC = moment
        .utc(dates.start.format("YYYY-MM-DD"))
        .startOf("day");
      const endDateUTC = moment
        .utc(dates.end.format("YYYY-MM-DD"))
        .endOf("day");

      return partnerData.filter((checkRecord) => {
        if (checkRecord.checkIn && checkRecord.checkOut) {
          const checkDateUTC = moment
            .utc(checkRecord.checkIn.assistanceCheck_checkDate)
            .startOf("day");

          if (
            !checkDateUTC.isSameOrAfter(startDateUTC, "day") ||
            !checkDateUTC.isSameOrBefore(endDateUTC, "day")
          ) {
            return false;
          }

          const hoursWorked = calcularTiempoTrabajado(
            checkRecord.checkIn.assistanceCheck_checkTime,
            checkRecord.checkOut.assistanceCheck_checkTime
          );
          return hoursWorked >= fullDayHours;
        }
        return false;
      }).length;
    } else if (detailedRecords && detailedRecords.length > 0) {
      const partnerRecords = getPartnerHoursByDate(
        detailedRecords,
        record.partnerId
      );

      const startDateUTC = moment
        .utc(dates.start.format("YYYY-MM-DD"))
        .startOf("day");
      const endDateUTC = moment
        .utc(dates.end.format("YYYY-MM-DD"))
        .endOf("day");

      return partnerRecords.filter((recordItem) => {
        const recordDateUTC = moment.utc(recordItem.date).startOf("day");
        if (
          !recordDateUTC.isSameOrAfter(startDateUTC, "day") ||
          !recordDateUTC.isSameOrBefore(endDateUTC, "day")
        ) {
          return false;
        }
        return recordItem.hoursWorked >= fullDayHours;
      }).length;
    } else if (record.realSchedule && record.realSchedule.length > 0) {
      const startDateUTC = moment
        .utc(dates.start.format("YYYY-MM-DD"))
        .startOf("day");
      const endDateUTC = moment
        .utc(dates.end.format("YYYY-MM-DD"))
        .endOf("day");

      return (
        record.realSchedule.filter((schedule) => {
          const scheduleDateUTC = moment
            .utc(schedule.exactDate || schedule.day)
            .startOf("day");
          if (
            !scheduleDateUTC.isSameOrAfter(startDateUTC, "day") ||
            !scheduleDateUTC.isSameOrBefore(endDateUTC, "day")
          ) {
            return false;
          }
          return Number(schedule.worked) >= fullDayHours;
        }).length || 0
      );
    } else {
      return record.daysWorked || 0;
    }
  }, [
    realCheckRecords,
    detailedRecords,
    record.partnerId,
    record.realSchedule,
    record.daysWorked,
    record.jornadasCompletas,
    dates.start.format("YYYY-MM-DD"),
    dates.end.format("YYYY-MM-DD"),
  ]);

  const legacyNormalHours = React.useMemo(() => {
    if (realCheckRecords && realCheckRecords.length > 0) {
      const formattedData = formatDataForReport(
        realCheckRecords,
        dates.start.format("YYYY-MM-DD"),
        dates.end.format("YYYY-MM-DD"),
        payrollBranchMeta
      );

      const partnerData = formattedData.filter(
        (checkRecord) => checkRecord.checkIn?.partner_id === record.partnerId
      );

      let totalNormalHours = 0;

      for (const checkRecord of partnerData) {
        if (checkRecord.checkIn && checkRecord.checkOut) {
          const schedule = record.realSchedule.find((s) => {
            const scheduleDay = moment(s.exactDate || s.day).format(
              "YYYY-MM-DD"
            );
            const checkDay = moment
              .utc(checkRecord.checkIn?.assistanceCheck_checkDate)
              .format("YYYY-MM-DD");
            return scheduleDay === checkDay;
          });

          if (schedule) {
            const entradaProgramada = schedule.start + ":00";
            const salidaProgramada = schedule.end + ":00";
            const entradaRegistrada =
              checkRecord.checkIn.assistanceCheck_checkTime;
            const salidaRegistrada =
              checkRecord.checkOut.assistanceCheck_checkTime;

            let horasTrabajadasEnHorario = 0;

            if (salidaProgramada === "00:00:00") {
              if (entradaRegistrada >= entradaProgramada) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  entradaRegistrada,
                  "24:00:00"
                );
              } else if (entradaRegistrada < entradaProgramada) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  entradaProgramada,
                  "24:00:00"
                );
              }
            } else {
              if (entradaRegistrada >= entradaProgramada) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  entradaRegistrada,
                  salidaProgramada
                );
              } else if (
                entradaRegistrada < entradaProgramada &&
                salidaRegistrada > entradaProgramada
              ) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  entradaProgramada,
                  salidaProgramada
                );
              }
            }

            totalNormalHours += horasTrabajadasEnHorario;
          }
        }
      }

      return formatPayrollHoursDecimal(totalNormalHours);
    } else if (detailedRecords && detailedRecords.length > 0) {
      const partnerRecords = getPartnerHoursByDate(
        detailedRecords,
        record.partnerId
      );

      let totalNormalHours = 0;

      for (const recordItem of partnerRecords) {
        const schedule = record.realSchedule?.find(
          (s) => moment(s.day).format("DD-MM-YYYY") === recordItem.date
        );

        if (schedule) {
          const entradaProgramada = schedule.start + ":00";
          const salidaProgramada = schedule.end + ":00";
          const entradaRegistrada = recordItem.checkIn;
          const salidaRegistrada = recordItem.checkOut;

          let horasTrabajadasEnHorario = 0;

          if (salidaProgramada === "00:00:00") {
            if (entradaRegistrada !== salidaRegistrada) {
              if (entradaRegistrada >= entradaProgramada) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  entradaRegistrada,
                  "24:00:00"
                );
              } else if (entradaRegistrada < entradaProgramada) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  entradaProgramada,
                  "24:00:00"
                );
              }
            }
          } else {
            if (entradaRegistrada !== salidaRegistrada) {
              if (entradaRegistrada >= entradaProgramada) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  entradaRegistrada,
                  salidaProgramada
                );
              } else if (
                entradaRegistrada < entradaProgramada &&
                salidaRegistrada > entradaProgramada
              ) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  entradaProgramada,
                  salidaProgramada
                );
              }
            }
          }

          totalNormalHours += horasTrabajadasEnHorario;
        }
      }

      return formatPayrollHoursDecimal(totalNormalHours);
    } else {
      return formatPayrollHoursDecimal(Number(record.authorizedHours) || 0);
    }
  }, [
    realCheckRecords,
    detailedRecords,
    record.partnerId,
    record.realSchedule,
    record.authorizedHours,
    dates.start.format("YYYY-MM-DD"),
    dates.end.format("YYYY-MM-DD"),
  ]);

  const legacyTotalHoursFromRealChecks = React.useMemo(() => {
    if (!realCheckRecords || realCheckRecords.length === 0) {
      return formatPayrollHoursDecimal(Number(record.totalHoursWorked) || 0);
    }

    const formattedData = formatDataForReport(
      realCheckRecords,
      dates.start.format("YYYY-MM-DD"),
      dates.end.format("YYYY-MM-DD"),
      payrollBranchMeta
    );

    const partnerData = formattedData.filter(
      (checkRecord) => checkRecord.checkIn?.partner_id === record.partnerId
    );

    const totalHours = partnerData.reduce((sum, checkRecord) => {
      if (checkRecord.checkIn && checkRecord.checkOut) {
        const hoursWorked = calcularTiempoTrabajado(
          checkRecord.checkIn.assistanceCheck_checkTime,
          checkRecord.checkOut.assistanceCheck_checkTime
        );
        return sum + hoursWorked;
      }
      return sum;
    }, 0);

    return formatPayrollHoursDecimal(totalHours);
  }, [
    realCheckRecords,
    record.partnerId,
    record.totalHoursWorked,
    dates.start.format("YYYY-MM-DD"),
    dates.end.format("YYYY-MM-DD"),
  ]);

  const legacyExtraHoursFromRealChecks = React.useMemo(() => {
    if (!realCheckRecords || realCheckRecords.length === 0) {
      if (detailedRecords && detailedRecords.length > 0) {
        return calculateExtraHoursLikeChart(
          detailedRecords,
          record.partnerId,
          record.realSchedule
        );
      } else if (record.realSchedule && record.realSchedule.length > 0) {
        return calculateExtraHoursLikeChart(
          detailedRecords,
          record.partnerId,
          record.realSchedule
        );
      } else {
        return parseFloat(String(record.extraHours)) || 0;
      }
    }

    const formattedData = formatDataForReport(
      realCheckRecords,
      dates.start.format("YYYY-MM-DD"),
      dates.end.format("YYYY-MM-DD"),
      payrollBranchMeta
    );

    const partnerData = formattedData.filter(
      (checkRecord) => checkRecord.checkIn?.partner_id === record.partnerId
    );

    let totalExtraHours = 0;

    for (const checkRecord of partnerData) {
      if (checkRecord.checkIn && checkRecord.checkOut) {
        const schedule = record.realSchedule.find((s) => {
          const scheduleDay = moment(s.exactDate || s.day).format("YYYY-MM-DD");
          const checkDay = moment
            .utc(checkRecord.checkIn?.assistanceCheck_checkDate)
            .format("YYYY-MM-DD");
          return scheduleDay === checkDay;
        });

        if (schedule) {
          const entradaProgramada = schedule.start + ":00";
          const salidaProgramada = schedule.end + ":00";
          const entradaRegistrada =
            checkRecord.checkIn.assistanceCheck_checkTime;
          const salidaRegistrada =
            checkRecord.checkOut.assistanceCheck_checkTime;

          const horasRegistradas = calcularTiempoTrabajado(
            entradaRegistrada,
            salidaRegistrada
          );

          let horasTrabajadasEnHorario = 0;

          if (salidaProgramada === "00:00:00") {
            if (entradaRegistrada >= entradaProgramada) {
              horasTrabajadasEnHorario = calcularTiempoTrabajado(
                entradaRegistrada,
                "24:00:00"
              );
            } else if (entradaRegistrada < entradaProgramada) {
              horasTrabajadasEnHorario = calcularTiempoTrabajado(
                entradaProgramada,
                "24:00:00"
              );
            }
          } else {
            if (entradaRegistrada >= entradaProgramada) {
              horasTrabajadasEnHorario = calcularTiempoTrabajado(
                entradaRegistrada,
                salidaProgramada
              );
            } else if (
              entradaRegistrada < entradaProgramada &&
              salidaRegistrada > entradaProgramada
            ) {
              horasTrabajadasEnHorario = calcularTiempoTrabajado(
                entradaProgramada,
                salidaProgramada
              );
            }
          }

          let diferencia = horasRegistradas - horasTrabajadasEnHorario;

          const scheduleDate = moment
            .utc(checkRecord.checkIn.assistanceCheck_checkDate)
            .format("YYYY-MM-DD");
          const scheduleWithIncidents = record.schedules.find(
            (s) =>
              moment(s.schedule_exactDate).format("YYYY-MM-DD") === scheduleDate
          );

          if (
            scheduleWithIncidents?.incidents &&
            scheduleHasRejectedExtraPayIncident(
              scheduleWithIncidents.incidents
            )
          ) {
            diferencia = 0;
          }

          if (diferencia > 0) {
            const extrasDia = Math.floor(diferencia);
            totalExtraHours += chartHorasExtrasExcludingDoubleShift(
              horasRegistradas,
              extrasDia,
              fullDayHours
            );
          }
        }
      }
    }

    return totalExtraHours;
  }, [
    realCheckRecords,
    detailedRecords,
    record.partnerId,
    record.realSchedule,
    record.schedules,
    dates.start.format("YYYY-MM-DD"),
    dates.end.format("YYYY-MM-DD"),
    fullDayHours,
  ]);

  const daysWorkedDisplay = chartSummary.totalDays || legacyDaysWorked;
  const completeDaysDisplay =
    chartDaysData?.detailed && chartDaysData.detailed.length > 0
      ? chartSummary.completeDays
      : legacyCompleteDaysWorked;
  const totalHoursDisplay =
    chartSummary.totalHours > 0
      ? formatPayrollHoursDecimal(chartSummary.totalHours)
      : legacyTotalHoursFromRealChecks;
  const normalHoursDisplay =
    chartSummary.hoursInSchedule > 0
      ? formatPayrollHoursDecimal(chartSummary.hoursInSchedule)
      : legacyNormalHours;
  const extraHoursDisplay =
    extraHoursFromPayments != null
      ? extraHoursFromPayments
      : chartDaysData?.detailed && chartDaysData.detailed.length > 0
        ? chartSummary.extraHours
        : legacyExtraHoursFromRealChecks;
  const doubleShiftDisplay =
    chartSummary.doubleShiftDays || record.doubleShifts;

  const hasAlignedPayDetail = Boolean(
    paymentDetailData?.dailyPayments?.length &&
      chartDaysData?.detailed &&
      chartDaysData.detailed.length > 0
  );

  /** Igual que PaymentDetail cuando hay detalle completo; si no, summary del backend. */
  const totalPayDisplay =
    hasAlignedPayDetail
      ? calculateTotalPayWithIncentives
      : record.totalPayWithIncentives || fallbackTotalPay;

  React.useEffect(() => {
    if (!onCuadroExportSnapshot) return;
    const chilitoText = `${toTitleCase(record.name || "")}${
      record.position ? `\n${record.position}` : ""
    }`;
    const extra = extraHoursDisplay;
    const t = formatHoursToTime(extra);
    const horasExtrasStr = extra > 0 ? `${extra} (${t}h)` : "0";
    const incidentsTotal = visibleIncidentsCount;

    onCuadroExportSnapshot(record.partnerId, {
      chilitoText,
      daysWorked: daysWorkedDisplay,
      jornadasCompletadas: Number(completeDaysDisplay) || 0,
      horasTrabajadas: String(totalHoursDisplay),
      horasNormales: String(normalHoursDisplay),
      turnosDobles: Number(doubleShiftDisplay) || 0,
      horasExtras: horasExtrasStr,
      incidentsTotal,
      pagoEstimado: String(totalPayDisplay),
    });

    return () => {
      onCuadroExportSnapshot(record.partnerId, null);
    };
  }, [
    onCuadroExportSnapshot,
    record.partnerId,
    record.name,
    record.position,
    daysWorkedDisplay,
    completeDaysDisplay,
    totalHoursDisplay,
    normalHoursDisplay,
    doubleShiftDisplay,
    extraHoursDisplay,
    visibleIncidentsCount,
    totalPayDisplay,
  ]);

  const deductionColumns = React.useMemo(
    () => splitPayrollDeductionTotalsForCuadro(record),
    [
      record.totalDeductions,
      record.tarjetasDeductions,
      record.totalDeductionsExcludingTarjetas,
      record.calculatedNewDeductions,
    ]
  );

  return (
    <React.Fragment>
      <tr style={isSpecial ? { borderLeft: "4px solid #f59e0b", background: "#fffbeb" } : {}}>
        <PartnerCell record={record} onEditSalary={handleEditSalary} />
        <td className="text-center">{record.salaryPerFortnight}</td>
        <td className="text-center"> {record.dailySalary}</td>
        <td className="text-center">{record.payPerHour}</td>
        <td className="text-center">{daysWorkedDisplay}</td>
        <td className="text-center">
          <button
            className="btn "
            style={{ backgroundColor: "#50B7C0", color: "white" }}
            onClick={() => openModal("daysWorked")}
          >
            {completeDaysDisplay}
          </button>
        </td>
        <td className="text-center">{totalHoursDisplay}</td>
        <td className="text-center">{normalHoursDisplay}</td>

        <td className="text-center">{doubleShiftDisplay}</td>

        <td className="text-center">
          {(() => {
            const extra = extraHoursDisplay;
            const t = formatHoursToTime(extra);
            return extra > 0 ? `${extra} (${t}h)` : "0";
          })()}
        </td>

        <td className="text-center">{record.freeDaysWorked}</td>
        <td className="text-center">{record.freeDaysEnjoyed}</td>

        <td className="text-center">{record.celebrityDaysWorked || 0}</td>
        <td className="text-center">{record.totalIncentives}</td>

        <td className="text-center">
          {formatPayrollMoneyAmount(deductionColumns.totalDeductionsWithoutTarjetas)}
        </td>
        <td className="text-center">
          {formatPayrollMoneyAmount(deductionColumns.tarjetas)}
        </td>
        <td className="text-center">
          <button
            className="btn btn-verde"
            onClick={() => openModal("totalIncidents")}
            disabled={visibleIncidentsCount === 0}
          >
            {visibleIncidentsCount}
          </button>
        </td>

        <td>
          <button
            className="btn btn-verde"
            onClick={() => openModal("paymentDetails")}
          >
            Detalles
          </button>
        </td>
        <td>{totalPayDisplay}</td>
      </tr>

      <ModalContainer
        open={modal.open}
        closeFn={() => setModal({ open: false, type: "" })}
        title={`Detalles de ${record.name}`}
        size={
          modal.type === "totalIncidents" || modal.type === "daysWorked"
            ? "xxl"
            : "xl"
        }
      >
        {{
          daysWorked: (
            <ChartDaysWorked
              partner={record.partnerId}
              dates={dates}
              branch={branch}
              authorizedSchedule={record.realSchedule}
              detailedRecords={detailedRecords}
              realCheckRecords={
                paymentDetailData?.realCheckRecords || realCheckRecords
              }
              extraIncidentDates={[...extraIncidentDates]}
              schedules={record.schedules}
              pendingLeaderReviewDates={visiblePendingDotDates}
              incidentCountSnapshot={incidentCountSnapshot}
              chartSavedIncidents={chartDaysData?.savedIncidents}
              initialChartPayload={
                chartDaysData
                  ? {
                      graph: chartDaysData.graph || [],
                      detailed: chartDaysData.detailed || [],
                      freeDaysDates: chartDaysData.freeDaysDates,
                      freeDaysEnjoyedDates: chartDaysData.freeDaysEnjoyedDates,
                      celebrityDaysDates: chartDaysData.celebrityDaysDates,
                      absenceRows: chartDaysData.absenceRows,
                      savedIncidents: chartDaysData.savedIncidents,
                    }
                  : undefined
              }
            />
          ),
          totalIncidents: (
            <TotalIncidents
              schedulesIds={record.schedulesIds}
              payroll={record}
              dates={dates}
              partner={record.partnerId}
              branch={branch}
              onRefreshPartner={refreshPartnerPayrollData}
              onDisplayCountChange={setModalIncidentsCount}
            />
          ),
          paymentDetails: (
            <PaymentDetail
              data={record}
              detailedRecords={detailedRecords}
              appliedBonuses={
                paymentDetailData?.calculatedBonuses?.length
                  ? paymentDetailData.calculatedBonuses
                  : record.calculatedBonuses || []
              }
              appliedMissingBoxes={record.calculatedMissingBoxes || []}
              appliedNewDeductions={
                paymentDetailData?.calculatedNewDeductions?.length
                  ? paymentDetailData.calculatedNewDeductions
                  : record.calculatedNewDeductions || []
              }
              dates={dates}
              realCheckRecords={
                paymentDetailData?.realCheckRecords || realCheckRecords
              }
              partner={record.partnerId}
              branch={branch}
              chartDaysDetailed={chartDaysData?.detailed}
              chartDaysGraph={chartDaysData?.graph}
              chartDaysSavedIncidents={chartDaysData?.savedIncidents}
              pendingLeaderReviewDates={visiblePendingDotDates}
              incidentCountSnapshot={incidentCountSnapshot}
              initialFetchedData={paymentDetailData}
              initialFetchedDataVersion={paymentDetailDataVersion}
              onOverridesSaved={async (payload) => {
                if (payload && paymentDetailData?.dailyPayments?.length) {
                  setPaymentDetailData((prev) => {
                    if (!prev?.dailyPayments?.length) return prev;
                    return {
                      ...prev,
                      dailyPayments: prev.dailyPayments.map((dp: any) => {
                        const dpDate = moment(dp.date, [
                          "YYYY-MM-DD",
                          "DD/MM/YYYY",
                          "DD-MM-YYYY",
                        ]).format("YYYY-MM-DD");
                        if (dpDate !== payload.workDate) return dp;
                        return {
                          ...dp,
                          payment: payload.payment,
                          conceptOverride: payload.concept,
                          hasPaymentOverride: true,
                        };
                      }),
                    };
                  });
                }
                if (onRefreshPartner) {
                  await onRefreshPartner(record.partnerId);
                }
              }}
            />
          ),
        }[modal.type] || null}
      </ModalContainer>

      <ModalContainer
        title="Modificar salario"
        open={modalSalary}
        closeFn={() => setModalSalary(false)}
        children={
          <UpdateSalary
            closeFn={() => setModalSalary(false)}
            refreshFn={handleRefreshSalary}
            setLoading={(value) => setLoadingSalary(value)}
            dataSalary={salaryData}
          />
        }
      />
    </React.Fragment>
  );
};

export default RowPayRoll;
